import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import { Box, Button, Divider, FormLabel, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import DialogActions from '@mui/material/DialogActions';
import { useTranslation } from 'react-i18next';
import RiskSourceDto from '../../../dtos/Hazards/RiskSourceDto';
import RiskSourceApiService from '../../../api/RiskSourceApiService';
import SaveButton from '../../Common/SaveButton';
import { useAsyncCallback } from '../../../hooks/useAsyncCallback';
import { useSnackbar } from '../../Common/SnackbarProvider';

const SOURCE_LOCALE = 'en';
const TRANSLATABLE_LOCALES = [
    { code: 'ru', label: 'Русский' },
    { code: 'fr', label: 'Français' },
];

export default function ModifyRiskSourceModal (props) {
    const { onClose, open, selectedRiskSource, riskSourceApiService, onCancel } = props;
    const { t } = useTranslation();
    const snackbar = useSnackbar();

    const [riskSourceName, setRiskSourceName] = useState('');
    const [translations, setTranslations] = useState({});
    const [manualLocales, setManualLocales] = useState([]);

    const [nameError, setNameError] = useState('');

    const isEdit = Boolean(selectedRiskSource?.id);

    const { run: handleSave, loading: saving } = useAsyncCallback(async () => {
        const riskSourceDto = new RiskSourceDto();
        riskSourceDto.id = selectedRiskSource?.id ?? 0;
        riskSourceDto.name = riskSourceName;
        riskSourceDto.names = { [SOURCE_LOCALE]: riskSourceName, ...translations };
        riskSourceDto.manualLocales = manualLocales;

        if(validateInfo() && selectedRiskSource && selectedRiskSource.id) {
            const updatedRiskSource = await riskSourceApiService.updateRiskSource(riskSourceDto);

            if(updatedRiskSource.success) {
                snackbar.success(t('modifyRiskSource.toast.updated'));
                resetModal();
            } else {
                snackbar.error(t('modifyRiskSource.toast.updateError', { error: updatedRiskSource.message }));
            }
        } else if(validateInfo()) {
            const newRiskSource = await riskSourceApiService.createRiskSource(riskSourceDto);

            if(newRiskSource.success) {
                snackbar.success(t('modifyRiskSource.toast.added'));
                resetModal();
            } else {
                snackbar.error(t('modifyRiskSource.toast.addError', { error: newRiskSource.message }));
            }
        }
    });

    function validateInfo() {
        const errors = [];

        if(riskSourceName === null || riskSourceName === '') {
            errors.push(t('modifyRiskSource.validation.nameRequired'));
        }

        setNameError(!riskSourceName);

        if(errors.length > 0) {
            snackbar.error(t('modifyRiskSource.validation.failed', { errors: errors.join(', ') }));
            return false;
        } else {
            return true;
        }
    }

    function resetModal() {
        setRiskSourceName('');
        setTranslations({});
        setManualLocales([]);
        onClose();
    }

    function onTranslationChange(locale, value) {
        setTranslations(prev => ({ ...prev, [locale]: value }));
        if (!manualLocales.includes(locale)) {
            setManualLocales(prev => [...prev, locale]);
        }
    }

    function onClearOverride(locale) {
        setTranslations(prev => {
            const next = { ...prev };
            delete next[locale];
            return next;
        });
        setManualLocales(prev => prev.filter(l => l !== locale));
    }

    const helperText = useMemo(() => isEdit
        ? t('modifyRiskSource.translations.helperEdit')
        : t('modifyRiskSource.translations.helperCreate'), [isEdit, t]);

    useEffect(() => {
        if(open) {
            setRiskSourceName(selectedRiskSource?.name ?? '');
            const incomingNames = selectedRiskSource?.names ?? {};
            const nonSource = Object.fromEntries(
                Object.entries(incomingNames).filter(([locale]) => locale !== SOURCE_LOCALE)
            );
            setTranslations(nonSource);
            setManualLocales(selectedRiskSource?.manualLocales ?? []);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRiskSource, open])

    return(
        <Dialog
            open={open}
            sx={{ '& .MuiDialog-paper': { width: '100%' } }}
            maxWidth={'lg'}
            fullWidth
        >
            <DialogTitle>{selectedRiskSource?.name ? t('modifyRiskSource.title.edit', { name: selectedRiskSource.name }) : t('modifyRiskSource.title.create')}</DialogTitle>
            <DialogContent dividers>
                <Box>
                    <Box sx={{ mb: 2 }}>
                        <FormLabel>{t('modifyRiskSource.field.name')}</FormLabel>
                        <br/>
                        <TextField
                            error={!!nameError}
                            sx={{ width: '100%' }}
                            autoComplete={'Risk Source Name'}
                            value={riskSourceName}
                            onChange={(event) => setRiskSourceName(event.target.value)}
                        />
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle1" fontWeight={600}>
                        {t('modifyRiskSource.translations.title')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                        {helperText}
                    </Typography>
                    <Stack spacing={1.5}>
                        {TRANSLATABLE_LOCALES.map(({ code, label }) => {
                            const isManual = manualLocales.includes(code);
                            return (
                                <Box key={code} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <FormLabel sx={{ minWidth: 90 }}>{label}</FormLabel>
                                    <TextField
                                        size="small"
                                        sx={{ flex: 1 }}
                                        value={translations[code] ?? ''}
                                        placeholder={isEdit ? '' : t('modifyRiskSource.translations.willGenerate')}
                                        onChange={(e) => onTranslationChange(code, e.target.value)}
                                        InputProps={{
                                            endAdornment: isManual ? (
                                                <Tooltip title={t('modifyRiskSource.translations.regenerateTooltip')}>
                                                    <IconButton size="small" onClick={() => onClearOverride(code)}>
                                                        <RefreshRoundedIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : null,
                                        }}
                                        helperText={isManual ? t('modifyRiskSource.translations.manualBadge') : t('modifyRiskSource.translations.autoBadge')}
                                    />
                                </Box>
                            );
                        })}
                    </Stack>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button autoFocus variant={'contained'} onClick={onCancel} disabled={saving}>{t('common.cancel')}</Button>
                <SaveButton loading={saving} variant={'contained'} onClick={handleSave}>{t('common.save')}</SaveButton>
            </DialogActions>
        </Dialog>
    );
}

ModifyRiskSourceModal.propTypes = {
    onClose: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
    selectedRiskSource: PropTypes.objectOf(RiskSourceDto),
    riskSourceApiService: PropTypes.objectOf(RiskSourceApiService).isRequired,
    onCancel: PropTypes.func.isRequired,
}
